import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Sparkles, AlertTriangle, ArrowUp, ArrowDown, Minus, Clock } from "lucide-react";

/**
 * Program-manager-first Key Insights scorecard.
 *
 * Fetches `GET /api/programs/{pid}/report/charts` which now returns:
 *   { cadence, tiles, data_quality, fallback }
 *
 * Each tile is information-dense (level badge · RAG status · big number ·
 * Δ vs prior period · baseline/target anchor bar · sparkline · AI caption).
 * No hero charts. The full quantitative dataset lives further down the
 * report behind a disclosure.
 */
export default function KeyInsights({ pid: pidProp }) {
  const params = useParams();
  const pid = pidProp || params.pid;
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!pid) { setState({ loading: false, data: null }); return; }
    let alive = true;
    setState({ loading: true, data: null });
    api.get(`/programs/${pid}/report/charts`)
      .then((r) => { if (alive) setState({ loading: false, data: r.data }); })
      .catch(() => { if (alive) setState({ loading: false, data: null }); });
    return () => { alive = false; };
  }, [pid]);

  if (state.loading) {
    return (
      <div className="border-t border-slate-100 px-6 pt-8 pb-2" data-testid="key-insights-loading">
        <p className="label-overline">Key Insights</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-56 bg-slate-50 border border-slate-200 rounded-sm animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!state.data) return null;
  const { cadence, tiles = [], data_quality, fallback } = state.data;

  if ((!tiles || tiles.length === 0) && !fallback) return null;

  return (
    <div className="border-t border-slate-100 px-6 pt-8 pb-2" data-testid="key-insights">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="label-overline">Key Insights</p>
          <h3 className="font-heading text-2xl mt-1">
            {fallback ? "Program summary" : "Scorecard"}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            What's on track, what needs attention. Every number traces to source rows.
          </p>
        </div>
        {cadence && <CadenceStrip cadence={cadence} />}
      </div>

      {data_quality && (
        <div
          className="mt-4 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm"
          data-testid="data-quality-warning"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{data_quality.message}</span>
        </div>
      )}

      {fallback ? (
        <FallbackSummary fallback={fallback} />
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="scorecard-grid">
          {tiles.map((t) => <KpiTile key={t.indicator_id} t={t} />)}
        </div>
      )}

      {!fallback && tiles.length > 0 && <RagLegend />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cadence strip
// ---------------------------------------------------------------------------
const CADENCE_LABEL = {
  quarterly: "Quarterly cadence",
  monthly: "Monthly cadence",
  weekly: "Weekly cadence",
  yearly: "Yearly cadence",
  custom: "Custom cadence",
};

function CadenceStrip({ cadence }) {
  const parts = [];
  parts.push(CADENCE_LABEL[cadence.kind] || "Custom cadence");
  if (cadence.periods_reported) {
    parts.push(`${cadence.periods_reported} period${cadence.periods_reported === 1 ? "" : "s"} reported`);
  }
  if (cadence.latest_period) parts.push(`latest ${cadence.latest_period}`);
  const ageLabel = formatAge(cadence.latest_update_age_days);
  if (ageLabel) parts.push(ageLabel);
  return (
    <div
      className={`inline-flex items-center gap-2 text-[11px] tracking-wider uppercase font-medium px-2.5 py-1 rounded-sm border ${
        cadence.stale ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-600"
      }`}
      data-testid="cadence-strip"
    >
      <Clock className="w-3 h-3" />
      <span>{parts.join(" · ")}</span>
      {cadence.stale && <span className="text-amber-700 normal-case tracking-normal">· stale</span>}
    </div>
  );
}

function formatAge(days) {
  if (days === null || days === undefined) return null;
  if (days <= 0) return "updated today";
  if (days === 1) return "1 day ago";
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

// ---------------------------------------------------------------------------
// KPI Tile
// ---------------------------------------------------------------------------
const RAG_STYLES = {
  healthy: { dot: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-50 border-emerald-200 text-emerald-800", label: "On track" },
  needs_push: { dot: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-50 border-amber-200 text-amber-900", label: "Needs push" },
  at_risk: { dot: "bg-rose-500", text: "text-rose-700", chip: "bg-rose-50 border-rose-200 text-rose-800", label: "At risk" },
  stale: { dot: "bg-slate-400", text: "text-slate-600", chip: "bg-slate-50 border-slate-200 text-slate-600", label: "Stale" },
};

function KpiTile({ t }) {
  const rag = RAG_STYLES[t.status.rag] || RAG_STYLES.needs_push;
  return (
    <div
      className="border border-slate-200 rounded-sm bg-white p-4 flex flex-col gap-3"
      data-testid={`kpi-tile-${t.code}`}
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${levelChip(t.level)}`}
            data-testid={`tile-level-${t.code}`}
          >
            {t.level}
          </span>
          <span className="font-mono text-[10px] tracking-wider uppercase text-slate-500 truncate">{t.code}</span>
        </div>
        <span
          title={t.status.reason}
          className={`inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${rag.chip}`}
          data-testid={`tile-rag-${t.code}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${rag.dot}`} />
          {rag.label}
        </span>
      </div>

      <h4 className="font-heading text-base font-medium leading-snug">{t.name}</h4>

      {/* Big number + delta */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-3xl font-semibold text-slate-900 leading-none">
            {fmtValue(t.latest_value, t.unit)}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Latest · <span className="font-mono">{t.latest_period}</span>
          </p>
        </div>
        {t.delta && <DeltaBadge d={t.delta} />}
      </div>

      {/* Anchor bar */}
      <AnchorBar anchor={t.anchor} />

      {/* Sparkline */}
      {t.sparkline?.length >= 2 && <Sparkline data={t.sparkline} />}

      {/* Caption */}
      {t.caption && (
        <div
          className="text-[12px] text-slate-700 pt-3 border-t border-slate-100 leading-relaxed flex items-start gap-2"
          data-testid={`tile-caption-${t.code}`}
        >
          {t.ai_polished && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[9px] tracking-wider uppercase font-medium px-1.5 py-0.5 rounded-sm bg-amber-400 text-slate-900 mt-0.5" title="Polished by Claude">
              <Sparkles className="w-2.5 h-2.5" /> AI
            </span>
          )}
          <span>{t.caption}</span>
        </div>
      )}
    </div>
  );
}

function levelChip(level) {
  switch ((level || "").toLowerCase()) {
    case "impact": return "bg-violet-50 border-violet-200 text-violet-800";
    case "outcome": return "bg-sky-50 border-sky-200 text-sky-800";
    case "output": return "bg-slate-100 border-slate-200 text-slate-700";
    case "activity": return "bg-slate-50 border-slate-200 text-slate-600";
    case "input": return "bg-slate-50 border-slate-200 text-slate-500";
    default: return "bg-slate-100 border-slate-200 text-slate-700";
  }
}

function DeltaBadge({ d }) {
  if (d.pct === null || d.pct === undefined) return null;
  const tone =
    d.direction === "up" ? "text-emerald-700" :
    d.direction === "down" ? "text-rose-700" :
    "text-slate-600";
  const Icon = d.direction === "up" ? ArrowUp : d.direction === "down" ? ArrowDown : Minus;
  return (
    <div className={`flex flex-col items-end text-[11px] ${tone}`} data-testid="delta-badge">
      <span className="inline-flex items-center gap-1 font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {Math.abs(d.pct).toFixed(Math.abs(d.pct) >= 10 ? 0 : 1)}%
      </span>
      <span className="text-slate-500">vs {d.vs_period}</span>
    </div>
  );
}

function AnchorBar({ anchor }) {
  if (!anchor) return null;
  if (anchor.kind === "single") {
    return (
      <div className="text-[11px] text-slate-500" data-testid="anchor-single">
        {anchor.summary}
      </div>
    );
  }
  if (anchor.kind === "target") {
    const pct = Math.max(0, Math.min(100, anchor.progress_pct || 0));
    return (
      <div data-testid="anchor-target">
        <div className="w-full h-2 bg-slate-100 rounded-sm overflow-hidden">
          <div
            className="h-full bg-slate-900"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[11px] text-slate-600 mt-1 font-mono">{anchor.summary}</p>
      </div>
    );
  }
  // baseline
  return (
    <div data-testid="anchor-baseline">
      <div className="flex items-center gap-2 text-[11px] text-slate-600">
        <span className="flex-1 font-mono">{anchor.summary}</span>
        {anchor.inferred && (
          <span
            className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200"
            title="No explicit target set — anchor inferred from the first reported value"
          >
            inferred
          </span>
        )}
      </div>
    </div>
  );
}

function Sparkline({ data }) {
  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100, h = 22, pad = 1;
  const points = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - ((d.value - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = data[data.length - 1];
  const lastX = pad + (w - pad * 2);
  const lastY = h - pad - ((last.value - min) / range) * (h - pad * 2);
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full h-6"
      data-testid="sparkline"
    >
      <polyline fill="none" stroke="#0F172A" strokeWidth="1" points={points} />
      <circle cx={lastX} cy={lastY} r="1.5" fill="#0F172A" />
    </svg>
  );
}

function fmtValue(v, unit) {
  if (v === null || v === undefined) return "—";
  const isPct = (unit || "").includes("%");
  const formatted = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return (
    <>
      {formatted}
      {unit && <span className="text-base text-slate-500 font-body font-normal ml-1.5">{isPct ? unit : unit}</span>}
    </>
  );
}

// ---------------------------------------------------------------------------
// RAG legend (secondary)
// ---------------------------------------------------------------------------
function RagLegend() {
  return (
    <div
      className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-slate-500"
      data-testid="rag-legend"
    >
      <span className="label-overline !text-slate-400">Status</span>
      {[
        ["healthy", "On track — ≥80% of target or ≥+10% vs baseline"],
        ["needs_push", "Needs push — progress but slow"],
        ["at_risk", "At risk — below target or below baseline"],
        ["stale", "Stale — no fresh data for 2+ cadence cycles"],
      ].map(([k, text]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${RAG_STYLES[k].dot}`} />
          {text}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fallback
// ---------------------------------------------------------------------------
function FallbackSummary({ fallback }) {
  return (
    <div className="mt-6 space-y-4" data-testid="key-insights-fallback">
      <p className="text-xs text-slate-500 italic">{fallback.reason}</p>
      {fallback.summary?.length > 0 && (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left border-b border-slate-200">
                <th className="px-4 py-2 font-medium w-24">Code</th>
                <th className="px-4 py-2 font-medium">Indicator</th>
                <th className="px-4 py-2 font-medium text-right w-32">Total</th>
                <th className="px-4 py-2 font-medium text-right w-24">Periods</th>
              </tr>
            </thead>
            <tbody>
              {fallback.summary.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.code}</td>
                  <td className="px-4 py-2">{r.name}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {Number.isInteger(r.total) ? r.total.toLocaleString() : r.total.toLocaleString(undefined, { maximumFractionDigits: 1 })} {r.unit}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-500 text-xs">{r.periods}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
